import { assertPaise, todayIST } from "@templeos/shared";
import type { ApiCartLine, ItemKind, PosFund, PosItem, SaleLine } from "./types.js";

/** One line in the counter cart. Prices shown here are for display; the server re-prices from the catalogue. */
export interface CartLine {
  key: string;
  type: "item" | "donation";
  itemId?: string;
  fundCode?: string;
  eligible80g?: boolean; // Catalogue hint only; final eligibility is determined by the server.
  kind: ItemKind | "donation";
  name: string;
  unitPrice: number;
  qty: number;
  maxQty: number;
  serviceDate: string | null;
  slot: string | null;
  slots: string[];
}
export type Cart = readonly CartLine[];

export class CartError extends Error {}

/** Minutes since midnight in India Standard Time. */
export function istMinutes(now: Date = new Date()): number {
  const [h, m] = now.toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour12: false }).split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

const toMin = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** The slot to sell now: the first slot that has not ended yet. Null when every slot today is over. */
export function pickSlot(slots: readonly string[], nowMin: number): string | null {
  return slots.find((s) => toMin(s.split("-")[1] ?? "00:00") > nowMin) ?? null;
}

/** Slots still open for sale today. */
export const openSlots = (slots: readonly string[], nowMin: number): string[] =>
  slots.filter((s) => toMin(s.split("-")[1] ?? "00:00") > nowMin);

export function addItem(cart: Cart, item: PosItem, qty = 1, now: Date = new Date()): Cart {
  if (!Number.isInteger(qty) || qty <= 0) throw new CartError("Quantity must be a whole number above zero.");
  let serviceDate: string | null = null;
  let slot: string | null = null;
  if (item.requiresDate) {
    serviceDate = todayIST(now);
    if (item.slots.length) {
      slot = pickSlot(item.slots, istMinutes(now));
      if (!slot) throw new CartError(`${item.name}: all of today's slots are over.`);
    }
  }
  const key = `${item.id}|${serviceDate ?? ""}|${slot ?? ""}`;
  const existing = cart.find((l) => l.key === key);
  const newQty = (existing?.qty ?? 0) + qty;
  if (newQty > item.perBookingMax) throw new CartError(`At most ${item.perBookingMax} of ${item.name} per bill.`);
  if (existing) return cart.map((l) => (l.key === key ? { ...l, qty: newQty } : l));
  return [...cart, {
    key, type: "item", itemId: item.id, kind: item.kind, name: item.name, unitPrice: item.price, qty,
    maxQty: item.perBookingMax, serviceDate, slot, slots: item.slots,
  }];
}

export function addDonation(cart: Cart, fund: PosFund, amount: number): Cart {
  assertPaise(amount);
  if (amount <= 0) throw new CartError("Enter a donation amount.");
  if (amount < fund.minAmount) throw new CartError(`The minimum for ${fund.name} is ₹${fund.minAmount / 100}.`);
  const key = `don|${fund.code}|${crypto.randomUUID()}`;
  return [...cart, { key, type: "donation", fundCode: fund.code, kind: "donation", name: `Donation - ${fund.name}`, unitPrice: amount, qty: 1,
    maxQty: 1, serviceDate: null, slot: null, slots: [], eligible80g: fund.eligible80g }];
}

/** Set a line's quantity; zero removes the line. */
export function setQty(cart: Cart, key: string, qty: number): Cart {
  if (!Number.isInteger(qty) || qty < 0) throw new CartError("Quantity must be a whole number.");
  const line = cart.find((l) => l.key === key);
  if (!line) return cart;
  if (qty === 0) return cart.filter((l) => l.key !== key);
  if (qty > line.maxQty) throw new CartError(`At most ${line.maxQty} of ${line.name} per bill.`);
  return cart.map((l) => (l.key === key ? { ...l, qty } : l));
}

export const changeQty = (cart: Cart, key: string, delta: number): Cart =>
  setQty(cart, key, Math.max(0, (cart.find((l) => l.key === key)?.qty ?? 0) + delta));

export function setSlot(cart: Cart, key: string, slot: string): Cart {
  const line = cart.find((l) => l.key === key);
  if (!line || !line.slots.includes(slot)) throw new CartError("Choose a valid time slot.");
  const newKey = `${line.itemId}|${line.serviceDate ?? ""}|${slot}`;
  if (cart.some((l) => l.key === newKey && l.key !== key)) throw new CartError("That slot is already in the bill; change its quantity instead.");
  return cart.map((l) => (l.key === key ? { ...l, slot, key: newKey } : l));
}

const isLine = (v: unknown): v is CartLine => {
  const l = v as Record<string, unknown> | null;
  if (!l || typeof l !== "object") return false;
  const ref = l.type === "item" ? typeof l.itemId === "string" : l.type === "donation" && typeof l.fundCode === "string";
  const optText = (x: unknown) => x === null || typeof x === "string";
  return ref && typeof l.key === "string" && typeof l.name === "string" && typeof l.kind === "string"
    && Number.isSafeInteger(l.unitPrice) && (l.unitPrice as number) >= 0 && Number.isInteger(l.qty) && (l.qty as number) > 0
    && Number.isInteger(l.maxQty) && optText(l.serviceDate) && optText(l.slot) && Array.isArray(l.slots);
};

/** A bill restored from localStorage: anything that is not a well-formed cart line is dropped, never trusted. */
export function parseCart(raw: string | null): Cart {
  try {
    const v: unknown = JSON.parse(raw ?? "[]");
    return Array.isArray(v) ? v.filter(isLine) : [];
  } catch {
    return [];
  }
}

/**
 * Bring a bill up to date with the current counter configuration: lines for items no longer sold, or dated before
 * today (a bill left overnight), are dropped, and prices and names follow the catalogue (a mid-day price or festival
 * surge change), because the server re-prices every line and refuses a bill whose payment does not match.
 * Returns the same array when nothing changed.
 */
export function reconcileCart(cart: Cart, items: readonly PosItem[], today: string): Cart {
  let changed = false;
  const next = cart.flatMap((l): CartLine[] => {
    if (l.type === "donation") return [l];
    const item = items.find((i) => i.id === l.itemId);
    if (!item || (l.serviceDate !== null && l.serviceDate < today) || l.qty > item.perBookingMax) {
      changed = true;
      return [];
    }
    if (item.price === l.unitPrice && item.name === l.name) return [l];
    changed = true;
    return [{ ...l, unitPrice: item.price, name: item.name, maxQty: item.perBookingMax }];
  });
  return changed ? next : cart;
}

export function cartTotal(cart: Cart): number {
  const total = cart.reduce((s, l) => s + l.unitPrice * l.qty, 0);
  assertPaise(total);
  return total;
}

export function toApiCart(cart: Cart): ApiCartLine[] {
  return cart.map((l) => (l.type === "donation"
    ? { type: "donation", fundCode: l.fundCode!, amount: l.unitPrice }
    : { type: "item", itemId: l.itemId!, qty: l.qty, serviceDate: l.serviceDate, slot: l.slot }));
}

export const toSaleLines = (cart: Cart): SaleLine[] =>
  cart.map((l) => ({ name: l.name, qty: l.qty, unitPrice: l.unitPrice, amount: l.unitPrice * l.qty, kind: l.kind, itemId: l.itemId,
    slot: l.slot, serviceDate: l.serviceDate, ...(l.type === "donation" ? { eligible80g: l.eligible80g } : {}) }));
